import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { SpanEvent } from "../../../../../../../schemas";
import { useIsEventHighlighted } from "../EventHighlightStore";
import { EventHeader, EventTooltip, createEventBadge } from "./EventComponents";

interface TraceEventItemProps {
  event: SpanEvent;
  startTime: number;
  eventId: string;
  className?: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function TraceEventItem({
  event,
  eventId,
  startTime,
  onMouseEnter,
  onMouseLeave,
  className,
}: TraceEventItemProps) {
  const isHighlighted = useIsEventHighlighted(eventId);

  const eventType = getEventType(event);
  const isExpandable =
    eventType !== "mcp.request.parsed" &&
    eventType !== "mcp.streaming.started" &&
    eventType !== "log" &&
    eventType !== "mcp.streaming.first_byte";

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className={cn(
        "contents group",
        className,
        isExpandable && "cursor-pointer",
      )}
      data-highlighted={isHighlighted}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={isExpandable ? () => setIsOpen(!isOpen) : undefined}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setIsOpen(!isOpen);
        }
      }}
      role={isExpandable ? "button" : undefined}
      tabIndex={isExpandable ? 0 : undefined}
    >
      <div
        className={cn(
          "grid grid-cols-[auto_1fr] pl-2",
          isExpandable && "group-hover:bg-muted/50",
        )}
      >
        {/* Timeline column */}
        <div className="flex flex-1 flex-col items-center h-full">
          <div className="flex-1 w-px bg-border transition-colors" />
          <div className="w-3 h-3 rounded-full border-2 border-primary bg-background shadow-sm flex-shrink-0 group-hover:border-primary group-hover:bg-primary transition-colors" />
          <div className="flex-1 w-px bg-border transition-colors" />
        </div>

        {/* Timing column */}
        <SpanTiming hoverBackground={isExpandable}>
          +{event.time - startTime} ms
        </SpanTiming>
      </div>

      {/* Content column */}
      <div
        className={cn(
          "min-w-0 flex items-center py-2 px-1",
          isExpandable && "group-hover:bg-muted/50",
        )}
      >
        {isExpandable ? (
          <div className="flex flex-col flex-1 gap-2">
            <div
              data-role="expand-trigger"
              className="flex items-center justify-stretch cursor-pointer gap-2"
            >
              <ItemHeader event={event} />
              <div className="flex items-center gap-1">
                {isOpen ? (
                  <ChevronDown className="h-3 w-3 text-foreground" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-foreground" />
                )}
              </div>
            </div>

            {isOpen && (
              <div>
                <ItemContent event={event} />
              </div>
            )}
          </div>
        ) : (
          <ItemHeader event={event} />
        )}
      </div>
    </div>
  );
}

// Legacy component - no longer used with new 3-column layout
export function SpanTiming({
  children,
  hoverBackground = false,
}: {
  children: ReactNode;
  hoverBackground?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-end px-2 text-sm py-2",
        hoverBackground && "group-hover:bg-muted/50",
      )}
    >
      <div className="text-muted-foreground text-xs font-mono">{children}</div>
    </div>
  );
}

// Event type detection helper
function getEventType(event: SpanEvent): string {
  // Log events from tail consumers
  if (event.name === "log") {
    return "log";
  }

  // MCP-specific events (actual event types found in the codebase)
  if (event.name === "mcp.request.parsed") {
    return "mcp.request.parsed";
  }

  if (event.name === "mcp.streaming.started") {
    return "mcp.streaming.started";
  }

  if (event.name === "mcp.streaming.chunk") {
    return "mcp.streaming.chunk";
  }

  if (event.name === "mcp.streaming.message") {
    return "mcp.streaming.message";
  }

  return "generic";
}

function ItemHeader({ event }: { event: SpanEvent }) {
  switch (event.name) {
    case "log":
      return <LogEventContent event={event} />;
    case "mcp.request.parsed":
      return <McpRequestParsedEventContent event={event} />;
    case "mcp.streaming.started":
      return <McpStreamingStartedEventContent event={event} />;
    case "mcp.streaming.chunk": {
      const chunk = {
        chunkNumber: event.attributes.chunk_number as number,
        chunkSize: event.attributes.chunk_size as number,
        timeSinceLastChunk: event.attributes.time_since_last_chunk as number,
        chunkContent: event.attributes.chunk_content as string,
        chunkTruncated: event.attributes.chunk_truncated as boolean,
        time: event.time,
      };

      const actions = [
        createEventBadge(`${event.attributes.chunk_size} bytes`),
      ];

      return (
        <EventHeader
          title={`Raw Chunk #${chunk.chunkNumber}`}
          meta={actions}
          className="text-foreground hover:bg-muted/20"
        />
      );
    }
    case "mcp.streaming.first_byte": {
      const actions = [
        <EventTooltip
          key="tooltip"
          content="This is when the first byte of the streaming response is sent."
        />,
      ];

      return (
        <EventHeader
          title="First Byte"
          meta={actions}
          className="text-foreground hover:bg-muted/20"
        />
      );
    }
    case "mcp.streaming.message": {
      const messageNumber = event.attributes?.message_count as number;
      const isError = event.attributes?.is_error as boolean;

      const meta = (
        <>
          {createEventBadge(
            `Message #${messageNumber}`,
            "outline",
            "bg-success/10 border-success/30",
          )}
          {createEventBadge(
            isError ? "Error" : "OK",
            isError ? "destructive" : "secondary",
          )}
          <EventTooltip
            key="tooltip"
            content="When the MCP finished handling the response (but "
          />
        </>
      );

      return (
        <EventHeader
          title="Tool Response Ready"
          meta={meta}
          className="text-success"
        />
      );
    }
    default: {
      const attributeCount = event.attributes
        ? Object.keys(event.attributes).length
        : 0;

      const actions = [
        createEventBadge("Generic Event"),
        ...(attributeCount > 0
          ? [createEventBadge(`${attributeCount} attributes`)]
          : []),
        <EventTooltip
          key="tooltip"
          content="This is an unrecognized event type. Expand to view attributes for more information."
        />,
      ];

      return (
        <EventHeader
          title={event.name}
          meta={actions}
          className="text-foreground hover:bg-muted/20"
        />
      );
    }
  }
}

// Main event content renderer
function ItemContent({ event }: { event: SpanEvent }) {
  // Event type detection with educational components
  const eventType = getEventType(event);

  eventType === "mcp.streaming.chunk" ||
    eventType === "mcp.streaming.message" ||
    eventType === "generic";

  switch (eventType) {
    case "mcp.streaming.chunk":
      return (
        <ChunkContent
          chunk={{
            chunkNumber: event.attributes.chunk_number as number,
            chunkSize: event.attributes.chunk_size as number,
            timeSinceLastChunk: event.attributes
              .time_since_last_chunk as number,
            chunkContent: event.attributes.chunk_content as string,
            chunkTruncated: event.attributes.chunk_truncated as boolean,
            time: event.time,
          }}
        />
      );
    case "mcp.streaming.message":
      return <McpMessageEventContent event={event} />;
    // case "mcp.streaming.completed":
    //   return <McpStreamingCompletedEventContent event={event} />;
    case "log":
      return <LogEventContent event={event} />;
    default: {
      // Still show some information about the event but we don't have a custom renderer for it
      console.warn("unrecognized event type", event);
      return (
        <>
          {/* <div className="text-sm text-muted-foreground mb-3">
            This is an unrecognized event type. Check the attributes below for
            more information.
          </div> */}
          {/* Event attributes */}
          {event.attributes && Object.keys(event.attributes).length > 0 && (
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Attributes:
              </div>
              {Object.entries(event.attributes).map(([key, value]) => (
                <div
                  key={key}
                  className="text-xs text-muted-foreground bg-muted/20 p-1 rounded"
                >
                  <span className="font-mono font-medium">{key}:</span>{" "}
                  <span>
                    {typeof value === "object"
                      ? JSON.stringify(value, null, 2)
                      : String(value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      );
    }
  }
}

// Get log level color and icon
const getLevelConfig = (level: string) => {
  switch (level.toLowerCase()) {
    case "error":
    case "fatal":
      return {
        badge: createEventBadge("ERROR", "destructive"),
        className: "text-destructive",
        bgColor: "bg-destructive/10 border-destructive/30",
      };
    case "warn":
    case "warning":
      return {
        badge: createEventBadge(
          "WARN",
          "outline",
          "bg-warning/10 border-warning/30 text-warning",
        ),
        className: "text-warning",
        bgColor: "bg-warning/10 border-warning/30",
      };
    case "debug":
      return {
        badge: createEventBadge(
          "DEBUG",
          "outline",
          "bg-muted/20 border-muted text-muted-foreground",
        ),
        className: "text-muted-foreground",
        bgColor: "bg-muted/10 border-muted/30",
      };
    default: // info, log
      return {
        badge: createEventBadge(
          "INFO",
          "outline",
          "bg-info/10 border-info/30 text-info",
        ),
        className: "text-info",
        bgColor: "bg-info/10 border-info/30",
      };
  }
};

// Log Event Content - Displays log messages from tail consumers
function LogEventContent({ event }: { event: SpanEvent }) {
  const logLevel = (event.attributes["log.level"] as string) || "info";
  const logMessage = (event.attributes["log.message"] as string) || event.name;

  // Parse message if it's still JSON-stringified (for backward compatibility)
  let displayMessage = logMessage;
  try {
    if (logMessage.startsWith('["') || logMessage.startsWith("[{")) {
      const parsed = JSON.parse(logMessage);
      displayMessage = Array.isArray(parsed)
        ? parsed.join(" ")
        : String(parsed);
    }
  } catch {
    // Keep original message if parsing fails
  }

  const levelConfig = getLevelConfig(logLevel);

  const meta = (
    <>
      {levelConfig.badge}
      <EventTooltip content="Log message captured from the application during execution via tail consumer" />
    </>
  );

  return (
    <EventHeader
      title={displayMessage}
      meta={meta}
      className={levelConfig.className}
    />
  );
}

function McpMessageEventContent({ event }: { event: SpanEvent }) {
  const contentCount = event.attributes?.content_count as number;
  const messageSize = event.attributes?.message_size as number;
  const method = event.attributes?.message_method as string;

  return (
    <>
      <div className="text-sm text-muted-foreground mb-3">
        This event marks when a complete JSON-RPC tool response became available
        for processing. The MCP server has successfully parsed a complete
        message from the streaming data.
      </div>
      {/* Message details grid */}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-muted-foreground">Content Items:</span>
          <span className="ml-2 font-mono text-foreground">{contentCount}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Size:</span>
          <span className="ml-2 font-mono text-foreground">{messageSize}B</span>
        </div>
        {method && (
          <div className="col-span-2">
            <span className="text-muted-foreground">Method:</span>
            <span className="ml-2 font-mono text-foreground text-xs">
              {method}
            </span>
          </div>
        )}
      </div>
    </>
  );
}

// MCP Request Parsed Event - Initial parsing of JSON-RPC request
function McpRequestParsedEventContent({ event }: { event: SpanEvent }) {
  const methodName = event.attributes["mcp.method.name"] as string;

  const meta = (
    <>
      {methodName &&
        createEventBadge(methodName, "outline", "bg-info/10 border-info/30")}
      <EventTooltip
        content="The MCP server successfully parsed the incoming JSON-RPC request and extracted the method information. This marks the beginning of request processing."
        icon={<Info className="h-3 w-3 text-info/80" />}
      />
    </>
  );

  return (
    <EventHeader title="Request Parsed" meta={meta} className="text-info" />
  );
}

// MCP Streaming Started Event - Beginning of streaming response
function McpStreamingStartedEventContent({ event }: { event: SpanEvent }) {
  const contentType = event.attributes?.content_type as string;

  const meta = (
    <>
      {contentType &&
        createEventBadge(contentType, "outline", "border-muted-foreground")}
      <EventTooltip content="The MCP server began sending a streaming response using Server-Sent Events. Data will arrive in chunks that need to be reconstructed into complete JSON-RPC messages." />
    </>
  );

  return (
    <EventHeader
      title="Streaming Started"
      meta={meta}
      className="text-foreground"
    />
  );
}

interface StreamingChunkData {
  chunkNumber: number;
  chunkSize: number;
  timeSinceLastChunk: number;
  chunkContent: string;
  chunkTruncated: boolean;
  time: number;
}

export function ChunkContent({ chunk }: { chunk: StreamingChunkData }) {
  return (
    <>
      <div className="text-sm text-muted-foreground mb-2">
        Raw streaming data chunk received from the MCP server. This content will
        be reconstructed into complete JSON-RPC messages.
      </div>

      <div className="bg-muted/20 p-2 rounded border">
        <pre className="text-xs overflow-x-auto whitespace-pre-wrap font-mono">
          {chunk.chunkContent}
        </pre>
      </div>

      {chunk.chunkTruncated && (
        <div className="mt-2 p-2 bg-warning/10 border border-warning/20 rounded text-xs text-warning">
          ⚠️ Chunk content was truncated for display
        </div>
      )}
    </>
  );
}
